import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, useParams } from "react-router-dom";
import SuccessBtn from "../Components/SuccessBtn";
import Moment from "react-moment";
import Select from "react-select";
import { Paper, Box } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PageShell from "./ModuleLayout/PageShell";
import { formatCustomerAddress } from "../utils/customerAddress";

function MakePayment() {
  let paymentTypeOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Card", value: "Card" },
    { label: "Check", value: "Check" },
    { label: "Online", value: "Online" },
  ];

  const getLocalDateInputValue = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = getLocalDateInputValue();
  const [receipt, setReceipt] = useState([]);
  const [payment, setPayment] = useState({ payment_date: today });
  const [errors, setErrors] = useState([]);
  const { receipt_id } = useParams();
  const [btnLoading, setBtnLoading] = useState(false);
  let [selectedPaymentType, setSelectedPaymentType] = useState("");

  const readableInvoiceNumber = useMemo(() => {
    if (receipt?.friendlyNumber) return receipt.friendlyNumber;
    if (receipt?.invoiceNumber) return String(receipt.invoiceNumber);
    if (receipt?._id) return `INV-${receipt._id.slice(-6).toUpperCase()}`;
    return "--";
  }, [receipt]);

  const formatMoney = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const num = Number(val);
    return Number.isNaN(num) ? val : num.toFixed(2);
  };

  let navigate = useNavigate();

  const getReceipt = useCallback(() => {
    axios
      .get(`${Helpers.baseUrl}/receipts/details/${receipt_id}`, Helpers.headers)
      .then((response) => {
        setReceipt(response.data);
        setPayment((prev) => ({
          ...prev,
          receipt_id: response.data._id,
          amount_due: response.data.remaining,
          amount_remaining: response.data.remaining,
          payment_date: prev.payment_date || today,
        }));
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  }, [navigate, receipt_id, today]);

  const handleSelectPaymentType = (selectedPaymentType) => {
    setSelectedPaymentType(selectedPaymentType);
    setPayment({ ...payment, payment_type: selectedPaymentType.value });
  };

  const updatePaidAmounts = (paidValue) => {
    const paid = paidValue;
    const due = parseFloat(payment.amount_due);
    const remaining = (due - paid).toFixed(2);
    let p_status = parseInt(remaining) > 0 ? "Partialy Paid" : "Paid";
    let org_paid = parseInt(receipt.paid);
    org_paid += parseFloat(paid);
    org_paid = org_paid.toFixed(2);
    setPayment({
      ...payment,
      amount_paid: paid,
      amount_remaining: remaining,
      paid: org_paid,
      status: p_status,
    });
  };

  const handlePaidAmount = (e) => updatePaidAmounts(e.target.value);
  const copyDueToPaid = () => updatePaidAmounts(payment.amount_due || 0);

  const handleSavePayment = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    axios
      .post(`${Helpers.baseUrl}/receipts/add-payment`, payment, Helpers.headers)
      .then(() => {
        Helpers.toast("success", "Payment Added successfully");
        setBtnLoading(false);
        navigate("/user/receipts");
      })
      .catch((error) => {
        getReceipt();
        setErrors(Helpers.error_response(error));
        setBtnLoading(false);
      });
  };

  useEffect(() => {
    getReceipt();
  }, [getReceipt]);

  const statusBadgeClass =
    receipt.status === "Paid"
      ? "badge badge-soft-success fs-11"
      : receipt.status === "Unpaid"
        ? "badge badge-soft-danger fs-11"
        : "badge badge-soft-warning fs-11";

  return (
    <PageShell
      title="Make Payment"
      description={`Invoice ${readableInvoiceNumber}${receipt.customer?.name ? ` · ${receipt.customer.name}` : ""}`}
      icon={require("../images/icons/receipt.png")}
      breadcrumbs={[
        {
          label: "Home",
          path: "/user/dashboard",
          icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
        },
        { label: "Receipts", path: "/user/receipts" },
        { label: "Unpaid", path: "/user/receipts/unpaid" },
        { label: "Make Payment", path: null },
      ]}
      summaryChips={receipt.status ? [{ label: receipt.status }] : []}
      variant="page"
    >
      <div className="row g-3 align-items-stretch">
        {/* ── LEFT: Receipt info ── */}
        <div className="col-lg-8 d-flex flex-column">
          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Meta row */}
            <Box
              sx={{
                p: 3,
                borderBottom: "1px dashed #dee2e6",
                background:
                  "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
              }}
            >
              <div className="row g-3">
                <div className="col-lg-3 col-6">
                  <p
                    className="text-muted mb-1 text-uppercase fw-semibold"
                    style={{ fontSize: 11 }}
                  >
                    Invoice No
                  </p>
                  <h6 className="mb-0">{readableInvoiceNumber}</h6>
                </div>
                <div className="col-lg-3 col-6">
                  <p
                    className="text-muted mb-1 text-uppercase fw-semibold"
                    style={{ fontSize: 11 }}
                  >
                    Date
                  </p>
                  <h6 className="mb-0">
                    <Moment date={receipt.date} format="MMM DD, YYYY" />
                  </h6>
                </div>
                <div className="col-lg-3 col-6">
                  <p
                    className="text-muted mb-1 text-uppercase fw-semibold"
                    style={{ fontSize: 11 }}
                  >
                    Payment Status
                  </p>
                  <span className={statusBadgeClass}>{receipt.status}</span>
                </div>
                <div className="col-lg-3 col-6">
                  <p
                    className="text-muted mb-1 text-uppercase fw-semibold"
                    style={{ fontSize: 11 }}
                  >
                    Total Amount
                  </p>
                  <h6 className="mb-0">$ {receipt.totalPrice}</h6>
                </div>
              </div>
            </Box>

            {/* Customer & Vehicle */}
            <Box sx={{ p: 3, borderBottom: "1px dashed #dee2e6" }}>
              <div className="row g-3">
                <div className="col-6">
                  <h6
                    className="text-muted text-uppercase fw-semibold mb-3"
                    style={{ fontSize: 11 }}
                  >
                    Customer Details
                  </h6>
                  <p className="fw-medium mb-1">
                    {receipt.customer?.name || ""}
                  </p>
                  <p className="text-muted mb-1">
                    {formatCustomerAddress(receipt.customer) || ""}
                  </p>
                  <p className="text-muted mb-1">
                    Phone: {receipt.customer?.phone || ""}
                  </p>
                  <p className="text-muted mb-0">
                    Email: {receipt.customer?.email || ""}
                  </p>
                </div>
                <div className="col-6">
                  <h6
                    className="text-muted text-uppercase fw-semibold mb-3"
                    style={{ fontSize: 11 }}
                  >
                    Vehicle Details
                  </h6>
                  {receipt.vehicle?.company && (
                    <p className="text-muted mb-1">
                      Company: {receipt.vehicle.company}
                    </p>
                  )}
                  <p className="text-muted mb-1">
                    Make: {receipt.vehicle?.make || ""}
                  </p>
                  <p className="text-muted mb-1">
                    Model: {receipt.vehicle?.model || ""}
                  </p>
                </div>
              </div>
            </Box>

            {/* Payment details + history */}
            <Box sx={{ p: 3 }}>
              <div className="row">
                <div className="col">
                  <h6
                    className="text-muted text-uppercase fw-semibold mb-3"
                    style={{ fontSize: 11 }}
                  >
                    Payment Details
                  </h6>
                  <p className="text-muted mb-1">
                    Status: <span className="fw-medium">{receipt.status}</span>
                  </p>
                  {receipt.status !== "Unpaid" && (
                    <p className="text-muted mb-1">
                      Method:{" "}
                      <span className="fw-medium">{receipt.paymentType}</span>
                    </p>
                  )}
                  <p className="text-muted mb-1">
                    Total:{" "}
                    <span className="fw-medium">$ {receipt.totalPrice}</span>
                  </p>
                  <p className="text-muted mb-1">
                    Paid: <span className="fw-medium">$ {receipt.paid}</span>
                  </p>
                  <p className="text-muted mb-0">
                    Remaining:{" "}
                    <span className="fw-medium text-danger">
                      $ {receipt.remaining}
                    </span>
                  </p>
                </div>

                {receipt.payments?.map((p, i) => (
                  <div className="col" key={i}>
                    <h6
                      className="text-muted text-uppercase fw-semibold mb-3"
                      style={{ fontSize: 11 }}
                    >
                      Payment History {i + 1}
                    </h6>
                    <p className="text-muted mb-1">
                      Date:{" "}
                      <span className="fw-medium">
                        <Moment date={p.payment_date} format="MMMM Do YYYY" />
                      </span>
                    </p>
                    <p className="text-muted mb-1">
                      Type: <span className="fw-medium">{p.payment_type}</span>
                    </p>
                    <p className="text-muted mb-1">
                      Due: <span className="fw-medium">$ {p.amount_due}</span>
                    </p>
                    <p className="text-muted mb-1">
                      Paid: <span className="fw-medium">$ {p.amount_paid}</span>
                    </p>
                    <p className="text-muted mb-0">
                      Remaining:{" "}
                      <span className="fw-medium">$ {p.amount_remaining}</span>
                    </p>
                  </div>
                ))}
              </div>
            </Box>
          </Paper>
        </div>

        {/* ── RIGHT: Payment form ── */}
        <div className="col-lg-4 d-flex flex-column">
          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                background:
                  "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
              }}
            >
              <h6 className="mb-0 fw-semibold">Add Payment Details</h6>
            </Box>
            <Box sx={{ p: 3, flex: 1 }}>
              <form>
                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Amount Due</label>
                  <input
                    type="number"
                    value={formatMoney(payment.amount_due)}
                    className="form-control"
                    readOnly
                  />
                  <small className="text-danger">
                    {errors.amount_due || ""}
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Amount Paid</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={payment.amount_paid}
                      onChange={handlePaidAmount}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={copyDueToPaid}
                      title="Copy Amount Due"
                    >
                      Copy Due
                    </button>
                  </div>
                  <small className="text-danger">
                    {errors.amount_paid || ""}
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">
                    Amount Remaining
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={formatMoney(payment.amount_remaining)}
                    readOnly
                  />
                  <small className="text-danger">
                    {errors.amount_remaining || ""}
                  </small>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Payment Type</label>
                  <Select
                    options={paymentTypeOptions}
                    value={selectedPaymentType}
                    onChange={handleSelectPaymentType}
                    isClearable="true"
                  />
                  <small className="text-danger">
                    {errors.payment_type || ""}
                  </small>
                </div>

                <div className="form-group mb-4">
                  <label className="form-label fw-semibold">Payment Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={payment.payment_date}
                    onChange={(e) =>
                      setPayment({ ...payment, payment_date: e.target.value })
                    }
                  />
                  <small className="text-danger">
                    {errors.payment_date || ""}
                  </small>
                </div>

                <SuccessBtn
                  text="Add Payment"
                  loading={btnLoading}
                  onClick={handleSavePayment}
                />
              </form>
            </Box>
          </Paper>
        </div>
      </div>
    </PageShell>
  );
}

export default MakePayment;
